import Axios, {AxiosRequestConfig, AxiosResponse} from 'axios';
import {tokenModule} from '@/store/authentication/authentication';
import router from '@/router';
import RetryCounter from '@/infrastructure/api/helper/RetryCounter';

export default class Auth {
    private internalErrorRetryCounter = new RetryCounter();
    private http = Axios.create();

    constructor() {
        this.http.interceptors.request.use(
            this.setupToken,
        );

        this.http.interceptors.response.use(
            this.succeedHandling.bind(this),
            this.errorHandling.bind(this),
        );
    }

    public async get<T = any, R = AxiosResponse<T>>(url: string, config?: AxiosRequestConfig): Promise<R> {
        return this.http.get(url, config);
    }

    public async post<T = any, R = AxiosResponse<T>>(url: string, data?: any, config?: AxiosRequestConfig): Promise<R> {
        return this.http.post(url, data, config);
    }

    public async put<T = any, R = AxiosResponse<T>>(url: string, data?: any, config?: AxiosRequestConfig): Promise<R> {
        return this.http.put(url, data, config);
    }

    public async delete<T = any, R = AxiosResponse<T>>(url: string, config?: AxiosRequestConfig): Promise<R> {
        return this.http.delete(url, config);
    }

    private setupToken(config: AxiosRequestConfig): AxiosRequestConfig {
        config.headers.Authorization = `Bearer ${tokenModule.アクセストークンを取得する}`;
        return config;
    }

    private succeedHandling(response: AxiosResponse): AxiosResponse {
        this.internalErrorRetryCounter.reset();
        return response;
    }

    private async errorHandling(error: any): Promise<unknown> {
        if (401 === error.response.status) {
            await tokenModule.再取得する();
            if (tokenModule.認証されている) {
                error.config.headers.Authorization = `Bearer ${tokenModule.アクセストークンを取得する}`;
                return this.http.request(error.config);
            }
            await router.push({name: 'expire'});
            return Promise.reject(error.response);
        }

        if (503 === error.response.status) {
            await router.push({name: 'maintenance'});
            return Promise.reject(error.response);
        }

        if (500 <= error.response.status) {
            if (this.internalErrorRetryCounter.isLimit()) {
                this.internalErrorRetryCounter.reset();
                await router.push({name: 'systemerror'});
                return Promise.reject(error.response);
            }
            if (error.config.method !== 'get') {
                await router.push({name: 'systemerror'});
                return Promise.reject(error.response);
            }
            setTimeout(this.http.request, 1000, error.config);
            this.internalErrorRetryCounter.countUp();
            return Promise.reject(error.response);
        }

        if (
            error.request.responseType === 'blob' &&
            error.response.data instanceof Blob &&
            error.response.data.type &&
            error.response.data.type.toLowerCase().indexOf('json') !== -1
        ) {
            return new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.onload = () => {
                    if (typeof reader.result === 'string') {
                        error.response.data = JSON.parse(reader.result);
                        resolve(Promise.reject(error.response));
                    } else {
                        return Promise.reject({
                            data: {
                                message: '不正な結果を受け取りました',
                            },
                        });
                    }
                };

                reader.onerror = () => {
                    reject(error);
                };

                reader.readAsText(error.response.data);
            });
        }
        return Promise.reject(error.response);
    }
}
